/**
 * Identity signatures over Space channel messages.
 *
 * Preimage is built via the shared helper so client signing and server ingest
 * stay byte-identical. Failed signatures are quarantined by the decrypt path
 * (body hidden); unsigned legacy messages are left unsigned.
 *
 * New sends use Ed25519+ML-DSA-65 when a PQ secret is provided. 64-byte
 * Ed25519 history still verifies.
 *
 * @module pages/spaces/spaceMessageSignature
 */

import { signIdentity, verifySignature, toBase64, fromBase64, toBytes } from '@adieuu/crypto';
import {
  buildSpaceMessageSignaturePreimage,
  type SpaceMessageSignatureContext,
} from '@adieuu/shared';

export function signSpaceMessage(
  signingPrivateKey: Uint8Array,
  context: SpaceMessageSignatureContext,
  ciphertextB64: string,
  nonceB64: string,
  cipherId: string,
  pqSigningPrivateKey?: Uint8Array,
): string {
  const preimage = buildSpaceMessageSignaturePreimage(
    context,
    ciphertextB64,
    nonceB64,
    cipherId,
  );
  return toBase64(signIdentity(signingPrivateKey, toBytes(preimage), pqSigningPrivateKey));
}

export function verifySpaceMessage(
  signingPublicKeyB64: string,
  context: SpaceMessageSignatureContext,
  ciphertextB64: string,
  nonceB64: string,
  cipherId: string,
  signatureB64: string,
  pqSigningPublicKeyB64?: string,
): boolean {
  try {
    const preimage = buildSpaceMessageSignaturePreimage(
      context,
      ciphertextB64,
      nonceB64,
      cipherId,
    );
    return verifySignature(
      fromBase64(signingPublicKeyB64),
      toBytes(preimage),
      fromBase64(signatureB64),
      pqSigningPublicKeyB64 ? fromBase64(pqSigningPublicKeyB64) : undefined,
    );
  } catch {
    return false;
  }
}
